/*
 * Real Sleep - personalized assessment results.
 * Reads the STOP-BANG answers as the patient clicks through the quiz and,
 * when the success screen appears, replaces it with a result matched to them:
 *   - diagnosed (with or without CPAP)  -> book a free consult
 *   - high / moderate / low risk        -> home sleep test offer
 * Self-contained: needs only #AssessmentResultsConfig on the page.
 * The contact step is a native Shopify form, so the page reloads on submit;
 * answers are stashed in sessionStorage just before that and read back after.
 * If anything fails, the original success screen stays as-is.
 */
(function () {
  'use strict';

  var section = document.getElementById('QuizSection');
  var config = document.getElementById('AssessmentResultsConfig');
  if (!section || !config) return;

  var successStep = section.querySelector('[data-step="success"]');
  if (!successStep) return;

  var d = config.dataset;
  var cfg = {
    testUrl: d.testUrl || '/products/at-home-sleep-apnea-test',
    consultUrl: d.consultUrl || '',
    code: d.code || '',
    price: d.price || '',
    regularPrice: d.regularPrice || '',
    yes: (d.yes || 'Yes').trim().toLowerCase()
  };

  var STORE_KEY = 'trs_assessment_results';
  var answers = {};
  var savedFields = {};
  var rendered = false;

  // Restore what was stashed before the form posted and the page reloaded.
  try {
    var saved = JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null');
    if (saved && saved.answers) {
      answers = saved.answers;
      savedFields = saved.fields || {};
    }
  } catch (err) { /* ignore */ }

  function stash() {
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify({
        answers: answers,
        fields: {
          QuizFirstName: fieldValue('QuizFirstName'),
          QuizHeight: fieldValue('QuizHeight'),
          QuizWeight: fieldValue('QuizWeight')
        }
      }));
    } catch (err) { /* storage unavailable; the default success screen will show */ }
  }

  // Record answers as they're chosen (same data the quiz itself uses).
  section.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('[data-quiz-answer]') : null;
    if (btn && btn.dataset.key) {
      answers[btn.dataset.key] = btn.dataset.value;
      stash();
    }
  });

  // Last chance to capture the typed fields before the native form navigates.
  section.addEventListener('submit', stash, true);

  function isYes(v) {
    return String(v || '').trim().toLowerCase() === cfg.yes;
  }

  function fieldValue(id) {
    var el = document.getElementById(id);
    var live = el ? String(el.value || '').trim() : '';
    return live || savedFields[id] || '';
  }

  // STOP-BANG: one point per factor, 0-8.
  function riskFactors() {
    var inches = parseFloat(fieldValue('QuizHeight'));
    var lbs = parseFloat(fieldValue('QuizWeight'));
    var bmi = inches > 0 && lbs > 0 ? (703 * lbs) / (inches * inches) : 0;
    var age = String(answers.age || '');
    var neck = String(answers.neck || '').trim();
    var gender = String(answers.gender || '').trim().toLowerCase();

    var factors = [
      [isYes(answers.snore), 'loud snoring'],
      [isYes(answers.tired), 'daytime fatigue'],
      [isYes(answers.observed), 'pauses in breathing during sleep'],
      [isYes(answers.blood_pressure), 'high blood pressure'],
      [bmi > 35, 'a BMI over 35'],
      [/^(50|65)/.test(age), 'age over 50'],
      [/^16/.test(neck) || /^over/i.test(neck), 'a neck size over 16"'],
      [gender === 'male', 'being male']
    ];
    return factors.filter(function (f) { return f[0]; }).map(function (f) { return f[1]; });
  }

  function joinFactors(list) {
    if (!list.length) return '';
    if (list.length === 1) return list[0];
    if (list.length === 2) return list[0] + ' and ' + list[1];
    var rest = list.length - 2;
    return list[0] + ', ' + list[1] + ', and ' + rest + ' other risk factor' + (rest === 1 ? '' : 's');
  }

  function buildResult() {
    var firstName = fieldValue('QuizFirstName');
    var eyebrow = firstName ? 'Your results, ' + firstName : 'Your results';
    var testCta = cfg.price ? 'Get my sleep test for ' + cfg.price : 'Get my sleep test';
    var consult = { label: 'Book my free consult', href: cfg.consultUrl, external: true };

    if (isYes(answers.diagnosed)) {
      if (isYes(answers.uses_cpap)) {
        return {
          level: 'cpap', eyebrow: eyebrow,
          title: 'There\u2019s a better option than your CPAP.',
          body: 'Many people who struggle with CPAP sleep well with a custom oral appliance \u2014 no mask, no hose, no noise. Book a free 15-minute call with one of our sleep doctors to see if you\u2019re a candidate.',
          cta: consult
        };
      }
      return {
        level: 'diagnosed', eyebrow: eyebrow,
        title: 'Let\u2019s find the right treatment for you.',
        body: 'Since you\u2019ve already been diagnosed with sleep apnea, the next step is talking through your options \u2014 including a custom oral appliance that treats sleep apnea without a CPAP mask. Book a free 15-minute call with one of our sleep doctors.',
        cta: consult
      };
    }

    var factors = riskFactors();
    var score = factors.length;
    var joined = joinFactors(factors);
    var secondary = cfg.consultUrl
      ? { label: 'Questions first? Book a free call with a sleep doctor', href: cfg.consultUrl, external: true }
      : null;
    var testLink = { label: testCta, href: cfg.testUrl, external: false };

    if (score >= 5) {
      return {
        level: 'high', meter: 3, score: score, eyebrow: eyebrow,
        title: 'High risk for sleep apnea.',
        body: 'Based on your answers \u2014 ' + joined + ' \u2014 we strongly recommend a sleep study to see what\u2019s happening while you sleep.',
        offer: true, cta: testLink, secondary: secondary
      };
    }
    if (score >= 3) {
      return {
        level: 'moderate', meter: 2, score: score, eyebrow: eyebrow,
        title: 'Moderate risk for sleep apnea.',
        body: 'Your answers show some common signs of sleep apnea, including ' + joined + '. A home sleep test is the simplest way to find out what\u2019s really happening while you sleep.',
        offer: true, cta: testLink, secondary: secondary
      };
    }
    return {
      level: 'low', meter: 1, score: score, eyebrow: eyebrow,
      title: 'Low risk for sleep apnea.',
      body: 'Based on your answers, your risk looks low. If you still snore, wake up tired, or someone has noticed you stop breathing at night, a home sleep test can give you a clear answer.',
      offer: true, cta: testLink, secondary: secondary
    };
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text !== undefined) n.textContent = text;
    return n;
  }

  function link(item, cls) {
    var a = el('a', cls);
    a.href = item.href || '#';
    if (item.external) { a.target = '_blank'; a.rel = 'noopener'; }
    return a;
  }

  function buildCard(r) {
    var card = el('div', 'quiz-card quiz-card--success ar-card ar-card--' + r.level);

    card.appendChild(el('p', 'ar-eyebrow', r.eyebrow));
    card.appendChild(el('h2', 'ar-title text-48', r.title));

    if (r.meter) {
      var meter = el('div', 'ar-meter ar-meter--' + r.level);
      var bar = el('div', 'ar-meter__bar');
      var labels = el('div', 'ar-meter__labels');
      ['Low', 'Moderate', 'High'].forEach(function (name, i) {
        bar.appendChild(el('span', 'ar-meter__seg' + (i < r.meter ? ' is-on' : '')));
        labels.appendChild(el('span', i === r.meter - 1 ? 'is-active' : '', name));
      });
      meter.appendChild(bar);
      meter.appendChild(labels);
      card.appendChild(meter);
      card.appendChild(el('p', 'ar-score', 'STOP-BANG score: ' + r.score + ' of 8'));
    }

    card.appendChild(el('p', 'ar-body text-18', r.body));

    if (r.offer) {
      var offer = el('div', 'ar-offer');
      offer.appendChild(el('p', 'ar-offer__label', 'Your next step'));
      offer.appendChild(el('p', 'ar-offer__name', 'Two-night home sleep test'));
      if (cfg.price) {
        var price = el('p', 'ar-offer__price');
        price.appendChild(el('span', 'ar-offer__now', cfg.price));
        if (cfg.regularPrice) {
          price.appendChild(document.createTextNode(' '));
          price.appendChild(el('s', 'ar-offer__was', cfg.regularPrice));
        }
        offer.appendChild(price);
      }
      if (cfg.code) {
        var note = el('p', 'ar-offer__note');
        note.appendChild(document.createTextNode('With code '));
        note.appendChild(el('strong', '', cfg.code));
        note.appendChild(document.createTextNode(' \u00b7 applied automatically \u00b7 shipping not included'));
        offer.appendChild(note);
      }
      card.appendChild(offer);
    }

    if (r.cta && r.cta.href) {
      var btn = link(r.cta, 'btn btn-primary ar-cta');
      var wrap = el('div', 'button__wrap');
      wrap.appendChild(el('span', '', r.cta.label));
      btn.appendChild(wrap);
      card.appendChild(btn);
    }

    if (r.secondary && r.secondary.href) {
      var sec = link(r.secondary, 'ar-secondary');
      sec.textContent = r.secondary.label;
      card.appendChild(sec);
    }

    card.appendChild(el('p', 'ar-disclaimer',
      'This screening is not a diagnosis. Only a sleep study can confirm sleep apnea.'));
    return card;
  }

  function resyncHeight() {
    var stage = document.getElementById('QuizStage');
    if (!stage) return;
    [0, 400, 900].forEach(function (t) {
      setTimeout(function () {
        stage.style.minHeight = successStep.scrollHeight + 'px';
      }, t);
    });
  }

  function render() {
    if (rendered || !Object.keys(answers).length) return;
    rendered = true;
    try {
      var r = buildResult();
      var card = buildCard(r);
      var original = successStep.querySelector('.quiz-card--success');
      if (original) {
        original.style.display = 'none';
        original.insertAdjacentElement('afterend', card);
      } else {
        successStep.appendChild(card);
      }
      resyncHeight();
      try { sessionStorage.removeItem(STORE_KEY); } catch (err) { /* ignore */ }
      if (typeof window.trsTrack === 'function') {
        window.trsTrack('assessment_result', { risk_level: r.level, stop_bang_score: r.score });
      }
    } catch (err) {
      console.warn('[AssessmentResults] falling back to default screen', err);
    }
  }

  new MutationObserver(function () {
    if (!successStep.hidden) render();
  }).observe(successStep, { attributes: true, attributeFilter: ['hidden'] });

  // After a native post the quiz shows the success step on load, possibly
  // before this observer exists.
  if (!successStep.hidden) render();
  document.addEventListener('DOMContentLoaded', function () {
    if (!successStep.hidden) render();
  });
})();
